import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { PaymentStatusBadge } from "@/components/admin/StatusBadge";
import MapsButton from "@/components/admin/MapsButton";
import { MarkPaidButton, WhatsAppButton } from "@/components/admin/QuickActions";
import { TBC_DATE } from "@/lib/bookingLabels";
import {
  BookingWithClient,
  computeDashboardStats,
  listBookings,
} from "@/lib/bookingsApi";
import {
  CATEGORY_LABELS,
  daysUntil,
  formatDisplayDate,
  formatTime,
  isOverdue,
} from "@/lib/bookingLabels";
import { formatGbpFromPence } from "@/types/database";
import { isSupabaseConfigured } from "@/lib/supabase";

const Group = ({ title, empty, items, onChange, showMaps = false }: { title: string; empty: string; items: BookingWithClient[]; onChange: () => void; showMaps?: boolean }) => (
  <section>
    <h2 className="font-playfair text-2xl mb-4">
      {title} {items.length > 0 && <span className="text-white/35 text-base font-inter">({items.length})</span>}
    </h2>
    {items.length === 0 ? (
      <p className="text-sm text-white/45 font-inter">{empty}</p>
    ) : (
      <ul className="space-y-3">
        {items.map((b) => (
          <li key={b.id} className="admin-card p-4">
            <Link to={`/admin/bookings/${b.id}`} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-base text-white truncate font-inter">{b.clients?.venue_or_client_name ?? "Untitled"}</p>
                <p className="text-sm text-white/55 font-inter mt-0.5">
                  {formatDisplayDate(b.performance_date)}
                  {b.start_time ? ` · ${formatTime(b.start_time)}${b.end_time ? `–${formatTime(b.end_time)}` : ""}` : ""}
                </p>
                <p className="text-xs text-white/40 font-inter mt-0.5">
                  {CATEGORY_LABELS[b.category]}
                  {b.clients?.contact_name ? ` · ${b.clients.contact_name}` : ""}
                  {b.clients?.phone ? ` · ${b.clients.phone}` : ""}
                </p>
              </div>
              <div className="text-right shrink-0 space-y-1">
                <p className="text-primary font-inter">{formatGbpFromPence(b.fee_pence)}</p>
                <PaymentStatusBadge paymentStatus={b.payment_status} bookingStatus={b.booking_status} dueDate={b.due_date} />
                {b.payment_status === "awaiting" && b.due_date && (
                  <p className={`text-xs font-inter ${isOverdue(b.payment_status, b.due_date) ? "text-destructive font-semibold" : "text-white/60"}`}>
                    {isOverdue(b.payment_status, b.due_date) ? `Overdue ${Math.abs(daysUntil(b.due_date))} days` : `Due by ${formatDisplayDate(b.due_date)}`}
                  </p>
                )}
              </div>
            </Link>
            {(showMaps || b.payment_status !== "paid") && (
              <div className="flex flex-wrap gap-2 mt-3">
                {showMaps && (b.address || b.venue_postcode || b.clients?.venue_or_client_name) && (
                  <MapsButton prominent destination={b.address || b.venue_postcode || b.clients?.venue_or_client_name || ""} />
                )}
                {b.payment_status !== "paid" && <MarkPaidButton booking={b} onDone={onChange} />}
              </div>
            )}
          </li>
        ))}
      </ul>
    )}
  </section>
);

const Dashboard = () => {
  const [rows, setRows] = useState<BookingWithClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(!isSupabaseConfigured);

  useEffect(() => {
    document.title = "Dashboard · Olivia Flemming Admin";
  }, []);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (tick === 0) setLoading(true);
      const res = await listBookings({ includeCancelled: true });
      if (!alive) return;
      if (res.ok === false) {
        setError(res.error);
        setUnconfigured(Boolean(res.unconfigured));
        setRows([]);
      } else {
        setError(null);
        setUnconfigured(false);
        setRows(res.data);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [tick]);

  const stats = useMemo(() => computeDashboardStats(rows), [rows]);
  const groups = useMemo(() => {
    const live = rows.filter((b) => b.booking_status === "scheduled");
    const dated = live.filter((b) => b.performance_date !== TBC_DATE);
    const today = dated.filter((b) => daysUntil(b.performance_date) === 0);
    const week = dated.filter((b) => { const d = daysUntil(b.performance_date); return d > 0 && d <= 7; });
    const later = dated.filter((b) => daysUntil(b.performance_date) > 7).slice(0, 4);
    const tbc = live.filter((b) => b.performance_date === TBC_DATE);
    const chase = live
      .filter((b) => b.payment_status === "awaiting" || b.payment_status === "attention")
      .sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
    return { today, week, later, tbc, chase };
  }, [rows]);
  const refresh = () => setTick((t) => t + 1);

  const cards = [
    { label: "Upcoming Bookings", value: String(stats.upcoming) },
    { label: "Awaiting Payment", value: String(stats.awaitingPayment) },
    { label: "Paid This Month", value: String(stats.paidThisMonth) },
    { label: "Needs Attention", value: String(stats.needsAttention) },
    { label: "Total Revenue", value: formatGbpFromPence(stats.totalRevenuePence) },
  ];

  return (
    <AdminShell
      title="Dashboard"
      actions={
        <Link
          to="/admin/bookings/new"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-black px-6 py-3 text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          New Booking
        </Link>
      }
    >
      {unconfigured && (
        <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90 font-inter">
          Supabase keys are not set yet. Dashboard UI is ready — connect{" "}
          <code className="text-primary">VITE_SUPABASE_URL</code> and{" "}
          <code className="text-primary">VITE_SUPABASE_ANON_KEY</code>, apply the Stage 2 migration, then refresh.
        </div>
      )}

      {error && !unconfigured && (
        <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200 font-inter">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-white/50 text-sm font-inter">Loading bookings…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 px-6 py-12 text-center">
          <p className="text-white/60 font-inter mb-4">No bookings yet.</p>
          <Link to="/admin/bookings/new" className="inline-flex rounded-full border border-primary/40 text-primary px-5 py-2.5 text-sm hover:bg-primary/10">
            Create your first booking
          </Link>
        </div>
      ) : (
        <div className="space-y-10">
          <section>
            <div className="flex items-end justify-between gap-4 mb-1">
              <h2 className="font-playfair text-2xl">Still to pay {groups.chase.some((b) => isOverdue(b.payment_status, b.due_date)) && <span className="ml-2 align-middle rounded-full bg-destructive/20 text-destructive text-xs font-inter px-2.5 py-1">{groups.chase.filter((b) => isOverdue(b.payment_status, b.due_date)).length} overdue</span>}</h2>
              <span className="text-sm text-white/45 font-inter">
                {formatGbpFromPence(groups.chase.reduce((s, b) => s + b.fee_pence, 0))} owed
              </span>
            </div>
            <p className="text-xs text-white/45 font-inter mb-4">Oldest due first. Overdue ones are in red.</p>
            {groups.chase.length === 0 ? (
              <p className="text-sm text-white/45 font-inter">Everyone has paid.</p>
            ) : (
              <ul className="space-y-3">
                {groups.chase.map((b) => {
                  const overdue = !!b.due_date && daysUntil(b.due_date) < 0;
                  return (
                    <li key={b.id} className={`admin-card p-4 ${overdue ? "border-destructive/60" : ""}`}>
                      <Link to={`/admin/bookings/${b.id}`} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-base text-white truncate font-inter">{b.clients?.venue_or_client_name}</p>
                          <p className="text-xs text-white/45 font-inter mt-0.5">{formatDisplayDate(b.performance_date)}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-primary font-inter">{formatGbpFromPence(b.fee_pence)}</p>
                          <p className={`mt-1 inline-block rounded-full px-2.5 py-1 text-xs font-inter font-medium ${overdue ? "bg-destructive/20 text-destructive" : "bg-white/5 text-white/70"}`}>
                            {b.due_date ? (overdue ? `Overdue ${Math.abs(daysUntil(b.due_date))} days` : `Due by ${formatDisplayDate(b.due_date)}`) : "No pay by date"}
                          </p>
                        </div>
                      </Link>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <WhatsAppButton booking={b} chase={overdue} compact />
                        <MarkPaidButton booking={b} onDone={refresh} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          <Group title="Today" empty="Nothing on today." items={groups.today} onChange={refresh} showMaps />
          <Group title="This week" empty="Nothing else in the next 7 days." items={groups.week} onChange={refresh} />
          {groups.tbc.length > 0 && <Group title="Date TBC" empty="" items={groups.tbc} onChange={refresh} />}
          {groups.later.length > 0 && (
            <div>
              <Group title="Later" empty="" items={groups.later} onChange={refresh} />
              <Link to="/admin/bookings" className="inline-block mt-3 text-sm text-primary hover:underline font-inter">See all bookings</Link>
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mt-12">
        {cards.map((c) => (
          <div
            key={c.label}
            className="rounded-2xl border border-white/10 bg-secondary/60 p-4 md:p-5"
          >
            <p className="text-[11px] uppercase tracking-widest text-white/45 font-inter mb-2 leading-snug">
              {c.label}
            </p>
            <p className="font-playfair text-2xl md:text-3xl text-white">{loading ? "—" : c.value}</p>
          </div>
        ))}
      </div>

    </AdminShell>
  );
};

export default Dashboard;
